import { useEffect, useRef, useState } from 'react';
import type {
  PaymentCardFormatter,
  PaymentCardFormatterContext,
} from '../../application/interfaces/PaymentSdkClient';
import type { PaymentService } from '../../application/services/PaymentService';
import type { BankItem } from '../../constants';
import type { PayMode } from './paymentHook.types';
import type { CardFormatterStateControls } from './useCardPaymentState';

const mapNetBankingMethods = (methods: unknown): BankItem[] => {
  if (!methods || typeof methods !== 'object') return [];

  return Object.entries(methods as Record<string, unknown>)
    .filter(([code, name]) => code.trim() && typeof name === 'string' && name.trim())
    .map(([code, name]) => ({
      code,
      name: String(name),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

type CardFormatterSetupOptions = {
  form: HTMLFormElement;
  initializedForms: WeakSet<HTMLFormElement>;
  setFormatter: (form: HTMLFormElement) => PaymentCardFormatter;
} & CardFormatterStateControls;

const setupCardFormatter = ({
  form,
  initializedForms,
  setFormatter,
  setCardNumber,
  setCardNumberError,
  setIsCardNumberValid,
  setCardNetwork,
  setCvvLength,
  setCardCvv,
  setCardExpiry,
  setIsRazorpayExpiryInvalid,
}: CardFormatterSetupOptions): void => {
  if (initializedForms.has(form)) return;

  const formatter = setFormatter(form);
  const cardEl = form.querySelector<HTMLInputElement>('[data-payvialink-card-number="true"]');
  const expiryEl = form.querySelector<HTMLInputElement>('[data-payvialink-expiry="true"]');
  const cvvEl = form.querySelector<HTMLInputElement>('[data-payvialink-cvv="true"]');
  let currentType = '';

  if (cardEl) {
    formatter
      .add('card', cardEl)
      .on('change', function (this: PaymentCardFormatterContext) {
        const value = this.el.value;
        const cleanNumber = value.replace(/\D/g, '');
        const maxLen = Number(this.maxLen) || (currentType === 'amex' ? 15 : 16);
        const isFullLength = cleanNumber.length === maxLen;
        const isValid = this.isValid();

        setCardNumber(value);
        setCardNumberError(isFullLength && !isValid ? 'Enter a valid card number' : null);
        setIsCardNumberValid(isValid);
      })
      .on('network', function (this: PaymentCardFormatterContext) {
        currentType = this.type || '';
        setCardNetwork(currentType);
        const expectedCvvLength = currentType === 'amex' ? 4 : 3;
        setCvvLength(expectedCvvLength);
        setCardCvv((current) => current.slice(0, expectedCvvLength));
        cvvEl?.setAttribute('maxlength', String(expectedCvvLength));
        cvvEl?.setAttribute('pattern', `^[0-9]{${expectedCvvLength}}$`);
      });
  }

  if (expiryEl) {
    formatter
      .add('expiry', expiryEl)
      .on('change', function (this: PaymentCardFormatterContext) {
        const value = this.el.value;
        setCardExpiry(value);
        setIsRazorpayExpiryInvalid(value.replace(/\D/g, '').length === 4 && !this.isValid());
      });
  }

  initializedForms.add(form);
};

interface UseRazorpayPaymentSetupOptions {
  service: PaymentService;
  razorpayKey: string;
  paymentMode: PayMode;
  formatterControls: CardFormatterStateControls;
}

export function useRazorpayPaymentSetup({
  service,
  razorpayKey,
  paymentMode,
  formatterControls,
}: UseRazorpayPaymentSetupOptions) {
  const [netBankingBanks, setNetBankingBanks] = useState<BankItem[] | null>(null);
  const [isRazorpayReady, setIsRazorpayReady] = useState(false);
  const [razorpayError, setRazorpayError] = useState<string | null>(null);
  const initializedCardFormsRef = useRef<WeakSet<HTMLFormElement>>(new WeakSet());

  useEffect(() => {
    if (!razorpayKey) return;

    let mounted = true;
    service
      .loadPaymentSdk()
      .then(() => {
        if (!mounted) return;
        setIsRazorpayReady(true);
        setRazorpayError(null);
      })
      .catch(() => {
        if (!mounted) return;
        setIsRazorpayReady(false);
        setRazorpayError('Razorpay SDK is not loaded.');
      });

    return () => {
      mounted = false;
    };
  }, [razorpayKey, service]);

  useEffect(() => {
    if (!razorpayKey || !isRazorpayReady) return;

    let mounted = true;

    service
      .onPaymentSdkReady(razorpayKey, (response) => {
        if (!mounted) return;
        const banks = mapNetBankingMethods(response?.methods?.netbanking);
        setNetBankingBanks(banks.length ? banks : null);
      })
      .catch(() => {
        if (mounted) setNetBankingBanks(null);
      });

    return () => {
      mounted = false;
    };
  }, [isRazorpayReady, razorpayKey, service]);

  useEffect(() => {
    const setFormatter = service.getCardFormatter();
    if (!isRazorpayReady || paymentMode !== 'card' || !setFormatter) return;

    document
      .querySelectorAll<HTMLFormElement>('[data-payvialink-card-form="true"]')
      .forEach((form) => {
        setupCardFormatter({
          form,
          initializedForms: initializedCardFormsRef.current,
          setFormatter,
          ...formatterControls,
        });
      });
  }, [formatterControls, isRazorpayReady, paymentMode, service]);

  return {
    netBankingBanks,
    razorpayError,
  };
}
